import { FC, useCallback, useMemo } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  SensorDescriptor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  SortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GraphQLTaggedNode, useMutation } from 'react-relay';

import { swapEdges, useErrorHandler } from 'lib';
import { MutationParameters } from 'relay-runtime';

type Edge = {
  node: {
    id: string;
  };
};

export type Position = {
  id: string;
  position: number;
};

type SortHandler = (
  positions: Position[],
  oldIndex: number,
  newIndex: number
) => any;

type Props = {
  edges: readonly Edge[];
  onSort: SortHandler;
  strategy?: SortingStrategy;
  sensors?: SensorDescriptor<any>[];
};

type SortFn = (
  positions: Position[],
  oldIndex: number,
  newIndex: number
) => Promise<any>;

type UseSorting = [handler: SortFn, sorting: boolean];

export function useSorting<M extends MutationParameters = MutationParameters>(
  mutation: GraphQLTaggedNode,
  connectionID?: string,
  makeVariables?: (positions: Position[]) => M['variables']
): UseSorting {
  const [sort, sorting] = useMutation<M>(mutation);
  const errorHandler = useErrorHandler();

  const handleSort = useCallback(
    (positions: Position[], oldIndex: number, newIndex: number) => {
      return new Promise((resolve, reject) => {
        if (!connectionID) return reject();

        sort({
          variables: makeVariables ? makeVariables(positions) : { positions },
          ...errorHandler({
            onCompleted: resolve,
            onError: reject,
          }),

          optimisticUpdater: store => {
            swapEdges(store, connectionID, oldIndex, newIndex);
          },

          updater: store => {
            // TODO: @Robustnes should we take into account the actual response from the server???
            swapEdges(store, connectionID, oldIndex, newIndex);
          },
        });
      });
    },
    [connectionID, errorHandler, makeVariables, sort]
  );

  return [handleSort, sorting];
}

const Sorting: FC<Props> = ({ children, edges, strategy, onSort, sensors }) => {
  const items = useMemo(
    () => edges.map(({ node: { id } }) => id) ?? [],
    [edges]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);

        onSort(
          arrayMove(items, oldIndex, newIndex).map((id, position) => ({
            id,
            position,
          })),
          oldIndex,
          newIndex
        );
      }
    },
    [items, onSort]
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}>
      <SortableContext
        strategy={strategy ?? verticalListSortingStrategy}
        items={items}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

export default Sorting;
